const featureSets = [
  ['Meetings', 'Transcription', 'Basic MOM', 'Email reminders', 'Standard support'],
  ['Meetings', 'Transcription', 'MOM', 'Reminders', 'SSO', 'Priority support'],
  ['Meetings', 'Transcription', 'MOM', 'Reminders', 'SSO', 'Advanced AI', 'Data residency', 'Dedicated CSM'],
  ['Custom SLAs', 'Private VPC', 'Custom integrations', 'Volume pricing', 'Named architect'],
];

/** Catalog with UI metadata (cards + matrix + history) */
export const planCatalog = [
  {
    id: 'plan-starter',
    name: 'Starter',
    price: 199,
    billingCycle: 'monthly',
    userLimit: 50,
    meetingQuota: 500,
    aiTokenQuota: 500000,
    storageGB: 25,
    features: featureSets[0],
    isActive: true,
    archived: false,
    version: '2025.1',
    activeTenantsCount: 18,
  },
  {
    id: 'plan-growth',
    name: 'Growth',
    price: 899,
    billingCycle: 'monthly',
    userLimit: 250,
    meetingQuota: 3000,
    aiTokenQuota: 3000000,
    storageGB: 200,
    features: featureSets[1],
    isActive: true,
    archived: false,
    version: '2025.1',
    activeTenantsCount: 16,
  },
  {
    id: 'plan-enterprise',
    name: 'Enterprise',
    price: 2499,
    billingCycle: 'monthly',
    userLimit: 2000,
    meetingQuota: 20000,
    aiTokenQuota: 20000000,
    storageGB: 2000,
    features: featureSets[2],
    isActive: true,
    archived: false,
    version: '2025.1',
    activeTenantsCount: 14,
  },
  {
    id: 'plan-custom',
    name: 'Custom',
    price: 0,
    priceLabel: 'Contact sales',
    billingCycle: 'custom',
    userLimit: null,
    meetingQuota: null,
    aiTokenQuota: null,
    storageGB: null,
    features: featureSets[3],
    isActive: true,
    archived: false,
    version: '2025.1',
    activeTenantsCount: 3,
  },
  {
    id: 'plan-legacy',
    name: 'Legacy Pro',
    price: 499,
    billingCycle: 'monthly',
    userLimit: 100,
    meetingQuota: 1200,
    aiTokenQuota: 1200000,
    storageGB: 80,
    features: ['Legacy bundle'],
    isActive: false,
    archived: true,
    version: '2023.4',
    activeTenantsCount: 2,
  },
];

/** Feature matrix: Starter | Growth | Enterprise columns */
export const planFeatureMatrix = [
  { key: 'users', label: 'Users limit', starter: '50', growth: '250', enterprise: '2000' },
  { key: 'meetings', label: 'Meetings/month', starter: '500', growth: '3,000', enterprise: '20,000' },
  { key: 'ai', label: 'AI Tokens', starter: '500K', growth: '3M', enterprise: '20M' },
  { key: 'storage', label: 'Storage GB', starter: '25', growth: '200', enterprise: '2,000' },
  { key: 'integrations', label: 'Integrations', starter: true, growth: true, enterprise: true },
  { key: 'sso', label: 'SSO', starter: false, growth: true, enterprise: true },
  { key: 'reports', label: 'Advanced reports', starter: false, growth: true, enterprise: true },
  { key: 'branding', label: 'Custom branding', starter: false, growth: false, enterprise: true },
];

export const planVersionHistory = [
  { version: '2025.1', publishedBy: 'billing@mh.ai', date: '2025-03-01T10:00:00Z', notes: 'Aligned quotas with usage engine v2.' },
  { version: '2024.12', publishedBy: 'billing@mh.ai', date: '2024-12-15T14:00:00Z', notes: 'Holiday pricing freeze; Enterprise storage bump.' },
  { version: '2024.8', publishedBy: 'revops@mh.ai', date: '2024-08-01T09:00:00Z', notes: 'Introduced Growth annual option (deprecated).' },
];

export const tenantSubscriptions = Array.from({ length: 20 }, (_, i) => {
  const plans = planCatalog.filter((p) => !p.archived && p.id !== 'plan-custom');
  const plan = plans[i % plans.length] || planCatalog[0];
  const daysToRenew = 28 + (i % 5) * 9;
  const renewal = new Date(Date.now() + daysToRenew * 86400000).toISOString();
  return {
    id: `sub-${3000 + i}`,
    tenantId: `tnt-${1000 + i}`,
    tenantCode: `MH-${String(i + 1).padStart(4, '0')}`,
    companyName: `Subscriber Co ${i + 1}`,
    planId: plan.id,
    planName: plan.name,
    status: i % 9 === 0 ? 'past_due' : i % 13 === 0 ? 'trial' : i % 17 === 0 ? 'cancelled' : 'active',
    contractTerm: i % 2 === 0 ? '12 months' : '36 months',
    billingCycle: plan.billingCycle === 'annual' ? 'annual' : 'monthly',
    currentPeriodStart: new Date(Date.now() - (i % 20) * 86400000).toISOString(),
    currentPeriodEnd: renewal,
    renewalDate: renewal,
    amountUsd: plan.billingCycle === 'annual' ? plan.price / 12 : plan.price,
    currency: 'USD',
    seats: plan.userLimit,
    autoRenew: i % 6 !== 0,
    commercialTerms: {
      poNumber: i % 4 === 0 ? `PO-${8000 + i}` : '',
      invoiceContactEmail: `billing${i}@subscriber.example`,
      discountPct: i % 5 === 0 ? 10 : 0,
      notes: i % 3 === 0 ? 'Net 45; VAT registered.' : '',
    },
  };
});

/** Calendar + pipeline: health for color coding */
export const renewalCalendarEvents = Array.from({ length: 18 }, (_, i) => {
  const days = [-5, 5, 12, 22, 35, 48][i % 6];
  const startsAt = new Date(Date.now() + days * 86400000);
  const endsAt = new Date(startsAt.getTime() + 3600000);
  const health = days < 0 ? 'overdue' : days < 30 ? 'at_risk' : 'healthy';
  const plan = planCatalog[i % 4];
  return {
    id: `rce-${i}`,
    tenantId: `tnt-${4100 + i}`,
    tenantCode: `MH-${String(4100 + i).padStart(4, '0')}`,
    companyName: `Calendar Tenant ${i + 1}`,
    ownerName: `Owner ${i + 1}`,
    eventType: i % 3 === 0 ? 'renewal' : 'upgrade_window',
    startsAt: startsAt.toISOString(),
    endsAt: endsAt.toISOString(),
    planName: plan.name,
    amountUsd: plan.price || 1200,
    mrrUsd: (plan.price || 1200) / (plan.billingCycle === 'annual' ? 12 : 1),
    renewalHealth: health,
  };
});

export const atRiskRenewalsSidebar = Array.from({ length: 6 }, (_, i) => ({
  id: `arr-${i}`,
  companyName: `At-Risk Co ${i + 1}`,
  usagePctLast30d: 35 + (i % 4) * 8,
  ownerName: `csm${i}@mh.ai`,
  recommendedAction: i % 2 === 0 ? 'Schedule QBR' : 'Offer training webinar',
}));
